import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'hooks';
import { CustomButton, If } from 'components';
import { CaretRightIcon } from 'assets/icons';
import { InvoicesRouteState } from 'routes/types';
import { FROM_SIDE_PANEL_ROUTE_STATE } from 'routes/constants';
import { clevertapEvents } from 'types/baseTypes';
import { logAnalyticEvent } from 'utils/analytics';
import { getAmountWithCurrency } from 'utils/amountUtils';
import { selectCurrentTeam } from 'containers/app/appSlice';
import routesPath from 'routes/RoutesPath';
import InvoiceCard from './InvoiceCard';
import { CustomerDetails, IInvoice } from '../invoices.model';
import { resetInvoiceState } from '../InvoicesSlice';

interface CustomerInvoiceWidgetProps {
  customerData: CustomerDetails;
  customerInvoicesList: IInvoice[];
  totalCustomerInvoicesCount: number;
}

const CustomerInvoiceWidget: React.FC<CustomerInvoiceWidgetProps> = ({
  customerData,
  customerInvoicesList,
  totalCustomerInvoicesCount
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const currentTeam = useAppSelector(selectCurrentTeam);

  const { rootInvoiceId, rootReceiptId, rootPage, rootCustomerId, rootDisputeId, rootJobId, rootEstimateId } =
    useParams();

  const handleInvoiceClick = invoice => {
    logAnalyticEvent(clevertapEvents.webCustomersListDetailsInvoiceLink, {
      'Customer Id': customerData?.id,
      'Invoice Id': invoice.invoiceId
    });
    if (rootCustomerId) {
      navigate(`/customers/${currentTeam?.id}/${rootCustomerId}/invoice/${invoice.invoiceId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootInvoiceId) {
      navigate(`/invoices/${currentTeam?.id}/${rootInvoiceId}/invoice/${invoice.invoiceId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootPage) {
      navigate(`/payouts/${rootPage}/${currentTeam?.id}/${rootPage}/invoice/${invoice.invoiceId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootReceiptId) {
      navigate(`/payments/receipts/${currentTeam?.id}/${rootReceiptId}/invoice/${invoice.invoiceId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootDisputeId) {
      navigate(`/payments/disputes/${currentTeam?.id}/${rootDisputeId}/invoice/${invoice.invoiceId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootJobId) {
      navigate(`/jobs/${currentTeam?.id}/${rootJobId}/invoice/${invoice.invoiceId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootEstimateId) {
      navigate(`/estimates/${currentTeam?.id}/${rootEstimateId}/invoice/${invoice.invoiceId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    }
  };

  const handleInvoiceNavigation = () => {
    const invoiceRouteState: InvoicesRouteState = {
      customerId: customerData.id,
      showSidePanel: false
    };
    navigate(routesPath.INVOICES, { replace: false, state: invoiceRouteState });
  };

  const summary = customerData
    ? [
        {
          id: 'customer-side-panel-summary-paid-invoices-amount',
          title: `Paid Invoices ${customerData?.paidInvoice.count ? `(${customerData?.paidInvoice?.count})` : ''}`,
          data: getAmountWithCurrency(customerData?.paidInvoice?.amount)
        },
        {
          id: 'customer-side-panel-summary-pending-invoices-amount',
          title: `Pending Invoices ${
            customerData?.pendingInvoice?.count ? `(${customerData?.pendingInvoice?.count})` : ''
          }`,
          data: getAmountWithCurrency(customerData?.pendingInvoice?.amount)
        }
      ]
    : [];

  return (
    <div className="mx-4 rounded-md border border-border">
      <div className="flex flex-col gap-3 border-b border-highlightGray px-4 py-3">
        <div className="flex justify-between text-sbase leading-[18px] tracking-[-0.3px]">
          <div className="font-bold text-primaryText">Invoices</div>
          <button className="font-semibold text-secondary" onClick={handleInvoiceNavigation}>
            See all
          </button>
        </div>
        <div className="flex gap-3">
          {summary.map((info, index) => (
            <div key={info.title + index} className="w-1/2 rounded-md border border-borderGray p-2">
              <div className="pb-1.5 text-px13 font-semibold leading-4 text-accentText">{info.title}</div>
              <div className="text-sbase font-bold leading-[18px] text-primaryText">{info.data}</div>
            </div>
          ))}
        </div>
      </div>
      <If condition={Boolean(totalCustomerInvoicesCount)}>
        <div className="flex flex-col gap-4 border-b border-borderGray p-4">
          {customerInvoicesList?.map(invoice => (
            <div
              id="customer-panel-invoice-details-card"
              key={invoice.invoiceId}
              className="border-b border-highlightGray last:border-b-0 only:border-b-0">
              <InvoiceCard
                data={invoice}
                onInvoiceClick={() => {
                  handleInvoiceClick(invoice);
                }}
              />
            </div>
          ))}
          <button
            className="flex justify-between text-sbase font-semibold leading-[18px] tracking-[-0.3px] text-secondary"
            onClick={handleInvoiceNavigation}>
            View all {totalCustomerInvoicesCount} invoices
            <CaretRightIcon className="path-fill-current text-gray-1" />
          </button>
        </div>
      </If>
      <div className="p-4">
        <CustomButton
          id="customer-details-create-job"
          className="h-10 w-full"
          childrenContainerStyles="text-17px font-semibold"
          onClick={() => {
            dispatch(resetInvoiceState());
            navigate('/invoices/create?customerId=' + customerData?.id);
          }}>
          Create invoice
        </CustomButton>
      </div>
    </div>
  );
};

export default CustomerInvoiceWidget;
